import { useState } from 'react';
import { Box, Container, Grid, Card, Typography, Button, Chip } from '@mui/material';
import { Check } from 'lucide-react';

export function Academics({ data }) {
  const primaryColor = data.primary_color || '#1e3a8a';
  const academicsList = Array.isArray(data.academics) ? data.academics : [];

  if (academicsList.length === 0) return null;

  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box id="academics" sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#ffffff' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: 6 }}>
          <Box sx={{ display: 'inline-block', px: 2, py: 0.5, borderRadius: 4, backgroundColor: `${primaryColor}15`, color: primaryColor, mb: 1.5 }}>
            <Typography variant="caption" fontWeight={700} letterSpacing="0.05em">
              CURRICULUM & STREAMS
            </Typography>
          </Box>
          <Typography variant="h2" fontWeight={800} color="#0f172a" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, mb: 1 }}>
            Academic Excellence
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Structured educational pathways tailored to nurture intellect, curiosity, and critical thinking.
          </Typography>
        </Box>

        {/* Modern Pill Segmented Tab Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <Box
            sx={{
              display: 'inline-flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 1,
              p: 0.8,
              backgroundColor: '#f1f5f9',
              borderRadius: 50,
              border: '1px solid #e2e8f0',
              maxWidth: '100%',
            }}
          >
            {academicsList.map((item, index) => {
              const label = typeof item === 'string' ? item : item.title;
              const isActive = activeTab === index;
              return (
                <Button
                  key={index}
                  onClick={() => setActiveTab(index)}
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    borderRadius: 50,
                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    fontWeight: 700,
                    textTransform: 'none',
                    transition: 'all 0.25s ease',
                    color: isActive ? '#ffffff' : '#64748b',
                    backgroundColor: isActive ? primaryColor : 'transparent',
                    boxShadow: isActive ? `0 4px 14px ${primaryColor}40` : 'none',
                    '&:hover': {
                      backgroundColor: isActive ? primaryColor : 'rgba(255, 255, 255, 0.7)',
                      color: isActive ? '#ffffff' : '#0f172a',
                    },
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* Tab Content Display */}
        {academicsList.map((item, index) => {
          if (index !== activeTab) return null;
          const isString = typeof item === 'string';
          const title = isString ? item : item.title;
          const grades = isString ? null : item.grades;
          const description = isString ? null : item.description;
          const features = isString ? [] : (item.features || []);

          return (
            <Card
              key={index}
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: 5,
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                boxShadow: '0 20px 40px rgba(0,0,0,0.04)',
              }}
            >
              <Box>
                {grades && (
                  <Chip
                    label={grades}
                    sx={{
                      backgroundColor: `${primaryColor}15`,
                      color: primaryColor,
                      fontWeight: 700,
                      mb: 2,
                      fontSize: '0.875rem',
                    }}
                  />
                )}
                <Typography variant="h3" fontWeight={800} color="#0f172a" sx={{ mb: 2, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
                  {title}
                </Typography>
                {description && (
                  <Typography variant="body1" color="#475569" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
                    {description}
                  </Typography>
                )}

                {features.length > 0 && (
                  <>
                    <Typography variant="subtitle1" fontWeight={800} color="#0f172a" sx={{ mb: 2 }}>
                      Key Highlights:
                    </Typography>
                    <Grid container spacing={2}>
                      {features.map((feat, fIdx) => (
                        <Grid size={{ xs: 12, sm: 6 }} key={fIdx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                backgroundColor: `${primaryColor}20`,
                                color: primaryColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Check size={14} strokeWidth={3} />
                            </Box>
                            <Typography variant="body2" fontWeight={600} color="#1e293b">
                              {feat}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </Box>
            </Card>
          );
        })}
      </Container>
    </Box>
  );
}

export default Academics;
